import { useRef, useState, FC } from "react";
import toast from "react-hot-toast";
import { FormikProps } from "formik";

import { CampaignI } from "@/lib/types/campaign";
import { checkIfClient } from "shared/lib/helpers";
import NewLeadForm, { CreateNewLeadFormI } from "./new-lead-form";
import { handleErrorMessage } from "@/helpers/error";
import Modal, { ModalChildrenFnPropsI } from "shared/ui/modal";
import { useApiClient } from "@/context/api-client";
import { useLocalStorage } from "@/context/local-storage";
import { AccountI } from "shared/lib/interfaces/account";

import { swallow } from "@/api/legacy-swallow";

interface CreateNewLeadModalPropsI {
  modalId?: string;
  account?: AccountI;
  campaign?: CampaignI;
  handleNewLeadSubmitAsBacklog?: (data: CreateNewLeadFormI) => Promise<boolean>;
  onSuccess?: () => void;
}

export const CREATE_NEW_LEAD_MODAL_ID = "create-new-lead-modal";

const CreateNewLeadModalContent: FC<
  ModalChildrenFnPropsI & CreateNewLeadModalPropsI
> = ({
  handleClose,
  campaign,
  account,
  handleNewLeadSubmitAsBacklog,
  onSuccess,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const formikRef = useRef<FormikProps<CreateNewLeadFormI>>(null);
  const apiClient = useApiClient();
  const storage = useLocalStorage();

  const submitNewLeadToAccountAsBacklogDefault = async (
    data: CreateNewLeadFormI
  ) => {
    if (campaign?.id && account?.id) {
      const resp = await swallow(
        apiClient.addLeadToAccount(campaign.id, account.id, {
          first_name: data.first_name,
          last_name: data.last_name,
          title: data.title,
          phone: data.phone,
          mobile: data.mobile,
          email: data.email,
          linkedin_url: data.linkedin_url,
          // Person location fields only (account already exists)
          person_city: data.person_city,
          person_state: data.person_state,
          person_country: data.person_country,
          person_postal_code: data.person_postal_code,
        })
      ).catch((e) => e);

      if (resp.status === 200) {
        toast.success("Success");
        return true;
      } else {
        handleErrorMessage(
          resp?.response?.data?.error_code as number,
          "Failed to add new lead. Please message us in the chat window on the bottom right so we can assist."
        );
      }
    }

    return false;
  };

  const onFormSubmitHandler = async (data: CreateNewLeadFormI) => {
    setIsDisabled(true);

    const handleLeadCreation = handleNewLeadSubmitAsBacklog
      ? handleNewLeadSubmitAsBacklog
      : submitNewLeadToAccountAsBacklogDefault;

    const isSubmitSuccess = await handleLeadCreation(data);

    if (isSubmitSuccess) {
      storage.newLeadModalPersistedValues = null;
      onSuccess?.();
      close();
    }

    setIsDisabled(false);
  };

  const close = () => {
    if (checkIfClient()) {
      formikRef.current?.resetForm();
      handleClose();
    }
  };

  return (
    <div className="relative min-w-[520px] overflow-hidden rounded-xl px-1">
      <NewLeadForm
        account={account}
        campaign={campaign}
        onSubmit={onFormSubmitHandler}
        onCancel={close}
        loading={isDisabled}
      />
    </div>
  );
};

const CreateNewLeadModal: FC<CreateNewLeadModalPropsI> = (props) => (
  <Modal
    id={props.modalId || CREATE_NEW_LEAD_MODAL_ID}
    title="Create New Lead"
    description="Add a new lead to this account"
  >
    {(modalProps) => <CreateNewLeadModalContent {...props} {...modalProps} />}
  </Modal>
);

export default CreateNewLeadModal;
